/**
 * Auth store
 *
 * Manages:
 * - Authentication status and current user
 * - JWT token persistence
 * - Logout flow
 */

import { resetPushDevice } from '@/features/notifications/device'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useClubStore } from './club'
import { useNavigatorStore } from './navigator'

const MASQUERADE_ORIGINAL_USER_KEY = 'masqueradeOriginalUser'
const MASQUERADE_ORIGINAL_TOKEN_KEY = 'masqueradeOriginalToken'
const PARTICIPANT_LIST_ACTIVE_SESSION_STORAGE_KEY = 'participantList.activeSession'

export interface AuthUser {
  id: string
  email: string
  name: string
  role?: string
  details?: Record<string, unknown>
}

const readStoredUser = (): AuthUser | null => {
  const storedUser = localStorage.getItem('user')
  if (!storedUser || storedUser === 'undefined' || storedUser === 'null') return null

  try {
    const user = JSON.parse(storedUser) as AuthUser
    return user && user.id && user.email ? user : null
  } catch {
    localStorage.removeItem('user')
    return null
  }
}

const readStoredMasqueradeOriginalUser = (): AuthUser | null => {
  const storedUser = localStorage.getItem(MASQUERADE_ORIGINAL_USER_KEY)
  if (!storedUser || storedUser === 'undefined' || storedUser === 'null') return null

  try {
    const user = JSON.parse(storedUser) as AuthUser
    return user && user.id && user.email ? user : null
  } catch {
    localStorage.removeItem(MASQUERADE_ORIGINAL_USER_KEY)
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(readStoredUser())
  const masqueradeOriginalUser = ref<AuthUser | null>(readStoredMasqueradeOriginalUser())
  const isAuthenticated = computed(() => !!user.value)
  const isMasquerading = computed(() => !!masqueradeOriginalUser.value)
  const token = ref(localStorage.getItem('token') || '')

  const setUser = (newUser: AuthUser) => {
    if (user.value && user.value.id !== newUser.id) resetPushDevice(token.value)
    user.value = newUser
    localStorage.setItem('user', JSON.stringify(newUser))
  }

  const setToken = (newToken: string) => {
    token.value = newToken
    localStorage.setItem('token', newToken)
    localStorage.setItem('jwt', newToken)
  }

  const setSession = (newToken: string, newUser: AuthUser) => {
    if (user.value && user.value.id !== newUser.id) resetPushDevice(token.value)
    setToken(newToken)
    setUser(newUser)
  }

  const beginMasquerade = (newToken: string, newUser: AuthUser) => {
    if (!masqueradeOriginalUser.value && user.value) {
      masqueradeOriginalUser.value = user.value
      localStorage.setItem(MASQUERADE_ORIGINAL_USER_KEY, JSON.stringify(user.value))
      localStorage.setItem(MASQUERADE_ORIGINAL_TOKEN_KEY, token.value)
    }
    setSession(newToken, newUser)
    useClubStore().clearClubState()
    useNavigatorStore().clearNavigationState()
    clearEventScopedState()
  }

  const stopMasquerade = () => {
    const originalUser = masqueradeOriginalUser.value
    const originalToken = localStorage.getItem(MASQUERADE_ORIGINAL_TOKEN_KEY)

    if (!originalUser || !originalToken) return false

    masqueradeOriginalUser.value = null
    localStorage.removeItem(MASQUERADE_ORIGINAL_USER_KEY)
    localStorage.removeItem(MASQUERADE_ORIGINAL_TOKEN_KEY)
    setSession(originalToken, originalUser)
    useClubStore().clearClubState()
    useNavigatorStore().clearNavigationState()
    clearEventScopedState()
    return true
  }

  const logout = () => {
    resetPushDevice(token.value)
    user.value = null
    token.value = ''
    localStorage.removeItem('token')
    localStorage.removeItem('jwt')
    localStorage.removeItem('user')
    localStorage.removeItem(MASQUERADE_ORIGINAL_USER_KEY)
    localStorage.removeItem(MASQUERADE_ORIGINAL_TOKEN_KEY)
    masqueradeOriginalUser.value = null
    useClubStore().clearClubState()
    useNavigatorStore().clearNavigationState()
    clearEventScopedState()
  }

  const restoreSession = async () => {
    if (token.value) {
      try {
        // Try to validate token and restore user (implement via useAuth composable)
        return true
      } catch {
        logout()
        return false
      }
    }
    return false
  }

  return {
    user,
    masqueradeOriginalUser,
    isAuthenticated,
    isMasquerading,
    token,
    setUser,
    setToken,
    setSession,
    beginMasquerade,
    stopMasquerade,
    logout,
    restoreSession,
  }
})

function clearEventScopedState() {
  localStorage.removeItem(PARTICIPANT_LIST_ACTIVE_SESSION_STORAGE_KEY)
}
